// src/components/Questionnaires/AccomplishmentInventoryPartA.js

import React, {useEffect, useState} from 'react';
import {Card, Form, Radio, Button, Alert, Typography, message, Spin} from 'antd';
import moment from 'moment';
import "./AccomplishmentInventoryPartA.css";

const {Title, Text} = Typography;

const AccomplishmentInventoryPartA = ({server_url, accessToken}) => {
    const [form] = Form.useForm();
    const [isEligible, setIsEligible] = useState(true);
    const [eligibilityMessage, setEligibilityMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [latestSubmission, setLatestSubmission] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch eligibility and latest submission on component mount
    useEffect(() => {
        const checkEligibility = async () => {
            try {
                // Check Eligibility
                const eligibilityResponse = await fetch(`${server_url}/questionnaires/accomplishment-inventory-partA/eligibility`, {
                    method: 'GET',
                    headers: {Authorization: `Bearer ${accessToken}`},
                });

                if (!eligibilityResponse.ok) {
                    throw new Error('Failed to check eligibility.');
                }

                const eligibilityData = await eligibilityResponse.json();
                setIsEligible(eligibilityData.eligible);
                setEligibilityMessage(eligibilityData.message);

                if (eligibilityData.eligible) {
                    // Fetch the latest submission
                    const latestResponse = await fetch(`${server_url}/questionnaires/accomplishment-inventory-partA/latest`, {
                        method: 'GET',
                        headers: {Authorization: `Bearer ${accessToken}`},
                    });

                    if (!latestResponse.ok) {
                        throw new Error('Failed to fetch latest submission.');
                    }

                    const latestData = await latestResponse.json();
                    if (latestData.latest_submission) {
                        setLatestSubmission(latestData.latest_submission);
                        form.setFieldsValue(latestData.latest_submission.qna);
                    }
                }
            } catch (err) {
                console.error(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        checkEligibility();
    }, [server_url, accessToken, form]);

    // Handle form submission
    const handleSubmit = async (values) => {
        setIsSubmitting(true);
        try {
            const response = await fetch(`${server_url}/questionnaires/accomplishment-inventory-partA/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    form_data: {
                        answers: values,
                    },
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to submit Accomplishment Inventory Part A.');
            }

            const data = await response.json();
            message.success('Accomplishment Inventory Part A submitted successfully!');
            setIsEligible(false);
            setEligibilityMessage(`Next eligible date: ${moment(data.next_eligible_date).format('YYYY-MM-DD')}`);
            // Optionally, you can reset the form or keep the latest submission displayed
        } catch (err) {
            console.error('Error submitting Accomplishment Inventory Part A:', err);
            message.error(err.message || 'An error occurred while submitting the form.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return <Spin tip="Loading..."/>;
    }

    if (error) {
        return <Alert message={error} type="error" showIcon/>;
    }

    return (
        <Card>

            <div className="ai-container">
                <h2 className="ai-header">PART A (5) - Accomplishments Inventory</h2>

                <p className="ai-description">This <strong>inventory</strong> will help you keep track of the
                    necessary <strong>steps</strong> and <strong>skills</strong> that you may need as you move through
                    school towards your next step.</p>
                <p className="ai-subdescription">Click the boxes below to mark them as completed!</p>

                <table className="ai-table">
                    <tr className="ai-tr-orange">
                        <th colSpan="3" className="ai-th">Have I gathered information about...</th>
                    </tr>
                    <tr className="ai-tr-white">
                        <td className="ai-td-orange"><input type="checkbox"/> Entrance exams I may appear for</td>
                        <td className="ai-td-orange"><input type="checkbox"/> College(s)/University(ies)/Vocational
                            Institutes I want to apply to
                        </td>
                        <td className="ai-td-orange"><input type="checkbox"/> Prospective job roles/opportunities</td>
                    </tr>
                    <tr className="ai-tr-darkorange">
                        <th colSpan="3" className="ai-th">Have I started preparation for...</th>
                    </tr>
                    <tr className="ai-tr-white">
                        <td className="ai-td-darkorange"><input type="checkbox"/> Entrance test(s) and/or interviews
                        </td>
                        <td className="ai-td-darkorange"><input type="checkbox"/> Filling out college/vocational
                            institute/professional institute forms
                        </td>
                        <td className="ai-td-darkorange"><input type="checkbox"/> Creating a portfolio highlighting my
                            skills and achievements
                        </td>
                    </tr>
                    <tr className="ai-tr-orange">
                        <th colSpan="3" className="ai-th">Have I acquired academic skills like...</th>
                    </tr>
                    <tr className="ai-tr-white">
                        <td className="ai-td-orange"><input type="checkbox"/> Thinking critically and creatively</td>
                        <td className="ai-td-orange"><input type="checkbox"/> Collaborating with peers and respecting
                            various perspectives
                        </td>
                        <td className="ai-td-orange"><input type="checkbox"/> Engaging in independent work/research</td>
                    </tr>
                    <tr className="ai-tr-darkorange">
                        <th colSpan="3" className="ai-th">Have I acquired life skills like...</th>
                    </tr>
                    <tr className="ai-tr-white">
                        <td className="ai-td-darkorange"><input type="checkbox"/> Understanding the value of money and
                            budgeting
                        </td>
                        <td className="ai-td-darkorange"><input type="checkbox"/> Managing stress for self and others
                        </td>
                        <td className="ai-td-darkorange"><input type="checkbox"/> Being safe and avoiding risky
                            behaviours
                        </td>
                    </tr>
                    <tr className="ai-tr-orange">
                        <th colSpan="3" className="ai-th">Have I worked to develop personal qualities like...</th>
                    </tr>
                    <tr className="ai-tr-white">
                        <td className="ai-td-orange"><input type="checkbox"/> Mutual respect and tolerance</td>
                        <td className="ai-td-orange"><input type="checkbox"/> Empathy</td>
                        <td className="ai-td-orange"><input type="checkbox"/> Resilience</td>
                    </tr>
                    <tr className="ai-tr-darkorange">
                        <th colSpan="3" className="ai-th">Additional Qualities</th>
                    </tr>
                    <tr className="ai-tr-white">
                        <td className="ai-td-darkorange"><input type="checkbox"/> Good citizenship</td>
                        <td className="ai-td-darkorange"><input type="checkbox"/> Appreciation for sustainability</td>
                        <td className="ai-td-darkorange"><input type="checkbox"/> Concern for society</td>
                    </tr>
                </table>
            </div>
            {/* Eligibility Message */}
            {/*<Alert*/}
            {/*    message={eligibilityMessage}*/}
            {/*    type={isEligible ? "success" : "warning"}*/}
            {/*    showIcon*/}
            {/*    style={{ marginBottom: 16 }}*/}
            {/*/>*/}

            {/* If Eligible, Show the Form */}
            {/*{isEligible ? (*/}
            {/*    <Form*/}
            {/*        layout="vertical"*/}
            {/*        form={form}*/}
            {/*        onFinish={handleSubmit}*/}
            {/*        initialValues={latestSubmission ? latestSubmission.qna : {}}*/}
            {/*    >*/}
            {/*        /!* Section 1: Information Gathering *!/*/}
            {/*        <Title level={4}>Section 1: Information Gathering</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="Have you explored the entrance exams available in your field of interest, including their format, eligibility, and application process?"*/}
            {/*            name="Have you explored the entrance exams available in your field of interest, including their format, eligibility, and application process?"*/}
            {/*            rules={[{ required: true, message: 'Please select an option.' }]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Have you thoroughly researched colleges, universities, or vocational institutes, including their courses, fees, and admission criteria?"*/}
            {/*            name="Have you thoroughly researched colleges, universities, or vocational institutes, including their courses, fees, and admission criteria?"*/}
            {/*            rules={[{ required: true, message: 'Please select an option.' }]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Do you have a clear understanding of the career roles or job opportunities related to your chosen field, including their skills requirements and growth potential?"*/}
            {/*            name="Do you have a clear understanding of the career roles or job opportunities related to your chosen field, including their skills requirements and growth potential?"*/}
            {/*            rules={[{ required: true, message: 'Please select an option.' }]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Section 2: Preparation *!/*/}
            {/*        <Title level={4}>Section 2: Preparation</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="Have you created a study plan or timetable to prepare for entrance tests or interviews, and are you following it consistently?"*/}
            {/*            name="Have you created a study plan or timetable to prepare for entrance tests or interviews, and are you following it consistently?"*/}
            {/*            rules={[{ required: true, message: 'Please select an option.' }]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Have you practiced filling out college, university, or vocational institute application forms, ensuring accuracy and attention to detail?"*/}
            {/*            name="Have you practiced filling out college, university, or vocational institute application forms, ensuring accuracy and attention to detail?"*/}
            {/*            rules={[{ required: true, message: 'Please select an option.' }]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Have you worked on creating a professional portfolio showcasing your skills, academic projects, extracurricular achievements, and certifications?"*/}
            {/*            name="Have you worked on creating a professional portfolio showcasing your skills, academic projects, extracurricular achievements, and certifications?"*/}
            {/*            rules={[{ required: true, message: 'Please select an option.' }]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Section 3: Academic Skills *!/*/}
            {/*        <Title level={4}>Section 3: Academic Skills</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="Do you actively engage in activities or exercises that help you think critically and creatively to solve problems or come up with new ideas?"*/}
            {/*            name="Do you actively engage in activities or exercises that help you think critically and creatively to solve problems or come up with new ideas?"*/}
            {/*            rules={[{ required: true, message: 'Please select an option.' }]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Have you participated in group projects or discussions where you demonstrated respect for diverse perspectives and opinions?"*/}
            {/*            name="Have you participated in group projects or discussions where you demonstrated respect for diverse perspectives and opinions?"*/}
            {/*            rules={[{ required: true, message: 'Please select an option.' }]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Have you undertaken independent research projects, written essays, or explored new areas of knowledge without external guidance?"*/}
            {/*            name="Have you undertaken independent research projects, written essays, or explored new areas of knowledge without external guidance?"*/}
            {/*            rules={[{ required: true, message: 'Please select an option.' }]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Are you skilled at using online tools and technology to search for and evaluate credible information for academic or personal purposes?"*/}
            {/*            name="Are you skilled at using online tools and technology to search for and evaluate credible information for academic or personal purposes?"*/}
            {/*            rules={[{ required: true, message: 'Please select an option.' }]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Do you regularly create and follow study schedules or manage your time effectively to complete tasks before deadlines?"*/}
            {/*            name="Do you regularly create and follow study schedules or manage your time effectively to complete tasks before deadlines?"*/}
            {/*            rules={[{ required: true, message: 'Please select an option.' }]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Are you able to follow complex instructions or make decisions when faced with unclear or ambiguous situations?"*/}
            {/*            name="Are you able to follow complex instructions or make decisions when faced with unclear or ambiguous situations?"*/}
            {/*            rules={[{ required: true, message: 'Please select an option.' }]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Section 4: Life Skills *!/*/}
            {/*        <Title level={4}>Section 4: Life Skills</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="Do you have a clear understanding of budgeting, such as planning your expenses and saving money for future needs?"*/}
            {/*            name="Do you have a clear understanding of budgeting, such as planning your expenses and saving money for future needs?"*/}
            {/*            rules={[{ required: true, message: 'Please select an option.' }]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Do you practice techniques to manage your own stress, such as mindfulness, exercise, or time management, and also help others cope with stress?"*/}
            {/*            name="Do you practice techniques to manage your own stress, such as mindfulness, exercise, or time management, and also help others cope with stress?"*/}
            {/*            rules={[{ required: true, message: 'Please select an option.' }]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Are you cautious about avoiding risky behaviors (e.g., unsafe internet practices, unhealthy habits) and prioritizing your personal safety?"*/}
            {/*            name="Are you cautious about avoiding risky behaviors (e.g., unsafe internet practices, unhealthy habits) and prioritizing your personal safety?"*/}
            {/*            rules={[{ required: true, message: 'Please select an option.' }]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Have you developed and consistently followed routines that support your physical, emotional, and mental well-being?"*/}
            {/*            name="Have you developed and consistently followed routines that support your physical, emotional, and mental well-being?"*/}
            {/*            rules={[{ required: true, message: 'Please select an option.' }]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Do you demonstrate honesty and integrity in following institutional rules, even when it might be inconvenient or challenging?"*/}
            {/*            name="Do you demonstrate honesty and integrity in following institutional rules, even when it might be inconvenient or challenging?"*/}
            {/*            rules={[{ required: true, message: 'Please select an option.' }]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Are you comfortable seeking help or guidance from teachers, mentors, or peers when faced with difficulties or challenges?"*/}
            {/*            name="Are you comfortable seeking help or guidance from teachers, mentors, or peers when faced with difficulties or challenges?"*/}
            {/*            rules={[{ required: true, message: 'Please select an option.' }]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Section 5: Personal Qualities *!/*/}
            {/*        <Title level={4}>Section 5: Personal Qualities</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="Do you show respect for people from different cultural, social, or educational backgrounds, and are you open to learning from their experiences?"*/}
            {/*            name="Do you show respect for people from different cultural, social, or educational backgrounds, and are you open to learning from their experiences?"*/}
            {/*            rules={[{ required: true, message: 'Please select an option.' }]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Do you actively empathize with others by understanding their emotions, perspectives, or challenges, and offering support when needed?"*/}
            {/*            name="Do you actively empathize with others by understanding their emotions, perspectives, or challenges, and offering support when needed?"*/}
            {/*            rules={[{ required: true, message: 'Please select an option.' }]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Have you demonstrated resilience by overcoming challenges, adapting to new situations, or learning from setbacks in your personal or academic life?"*/}
            {/*            name="Have you demonstrated resilience by overcoming challenges, adapting to new situations, or learning from setbacks in your personal or academic life?"*/}
            {/*            rules={[{ required: true, message: 'Please select an option.' }]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Have you contributed positively to your community, such as volunteering, participating in civic activities, or helping others in need?"*/}
            {/*            name="Have you contributed positively to your community, such as volunteering, participating in civic activities, or helping others in need?"*/}
            {/*            rules={[{ required: true, message: 'Please select an option.' }]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Do you take conscious steps to promote sustainability in your daily life, such as reducing waste, conserving resources, or supporting eco-friendly initiatives?"*/}
            {/*            name="Do you take conscious steps to promote sustainability in your daily life, such as reducing waste, conserving resources, or supporting eco-friendly initiatives?"*/}
            {/*            rules={[{ required: true, message: 'Please select an option.' }]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label="Are you aware of and concerned about social issues, and do you actively seek ways to contribute or raise awareness about them?"*/}
            {/*            name="Are you aware of and concerned about social issues, and do you actively seek ways to contribute or raise awareness about them?"*/}
            {/*            rules={[{ required: true, message: 'Please select an option.' }]}*/}
            {/*        >*/}
            {/*            <Radio.Group>*/}
            {/*                <Radio value="Yes">Yes</Radio>*/}
            {/*                <Radio value="No">No</Radio>*/}
            {/*            </Radio.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Submit Button *!/*/}
            {/*        <Form.Item>*/}
            {/*            <Button type="primary" className="bg-yellow-500 hover:bg-yellow-600 text-black" htmlType="submit" loading={isSubmitting}>*/}
            {/*                Submit*/}
            {/*            </Button>*/}
            {/*        </Form.Item>*/}
            {/*    </Form>*/}
            {/*) : (*/}
            {/*    <Text type="warning">*/}
            {/*        You can only submit this form once every 15 days. Please come back later.*/}
            {/*    </Text>*/}
            {/*)}*/}

            {/* Display Latest Submission */}
            {latestSubmission && isEligible && (
                <Card title="Your Latest Submission" style={{marginTop: 24}}>
                    {Object.entries(latestSubmission.qna).map(([question, answer], index) => (
                        <div key={index} style={{marginBottom: 16}}>
                            <Text strong>{question}</Text>
                            <br/>
                            <Text>{answer}</Text>
                        </div>
                    ))}
                </Card>
            )}
        </Card>
    );

};

export default AccomplishmentInventoryPartA;
